import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useStatsigDynamicHolidayConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-holiday-config.hook'
import QuinceIcon from '@src/quince-components/src/atoms/icon'
import { transformImageSrc } from '@src/quince-components/src/atoms/image/image.utils'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import type { RenderErrorMessageType, DisplayTextType } from '@src/quince-components/src/atoms/productCTA/types'
import ErrorStateIcon from '@src/quince-components/src/atoms/productForm/icons/errorState.inline.svg'
import OutOfStockIcon from '@src/quince-components/src/atoms/productForm/icons/outOfStock.inline.svg'
import CheckIcon from '@src/quince-components/src/atoms/select/icons/check_circle.svg'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import {
  deliveryTextCss,
  displayTextCss,
  displayTextQuickATCCss,
  errorIconCss,
  errorMessageCss,
  errorMessageLeftAlignCss,
  errorMessageQuickATCCss,
  errorRowCss,
  holidayIconCss,
  holidayMessageRowCss,
  inStockContentCss,
  inStockIconCss,
  inStockMessageCss,
  oosIconCss,
} from './product-cta-ds.styles'

/**
 * DS twin of `RenderErrorMessage` from legacy productCTA.tsx.
 * Renders the `ErrorStateIcon` unless the icon is suppressed (gold-bar errors hide the icon,
 * matching legacy `hideIcon`).
 */
export const RenderErrorMessageDS = ({
  errorMessage,
  className,
  hideIcon,
  isFromCartPopup,
  isQuickATCForMobile,
  showNewPdpDesignV2,
}: RenderErrorMessageType) => {
  // Mirror legacy RenderErrorMessage: V2 row layout, cart-popup left-align and the error icon are
  // all gated on showNewPdpDesignV2 (the icon additionally on !hideIcon for gold-bar errors).
  const showIcon = showNewPdpDesignV2 && !hideIcon
  return (
    <div
      role="alert"
      className={cx(
        errorMessageCss,
        showNewPdpDesignV2 && isFromCartPopup ? errorMessageLeftAlignCss : undefined,
        isQuickATCForMobile ? errorMessageQuickATCCss : undefined,
        showNewPdpDesignV2 ? errorRowCss : undefined,
        className,
      )}
    >
      {showIcon ? <QuinceIcon iconSvg={ErrorStateIcon} className={errorIconCss} /> : null}
      {errorMessage}
    </div>
  )
}

/**
 * DS twin of legacy `InStockAndReadyToShip`. Shows the check icon on mobile only.
 */
export const InStockAndReadyToShipDS = ({ className }: { className?: string }) => {
  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  return (
    <div className={cx(inStockMessageCss, className)}>
      <div className={inStockContentCss}>
        {isMobile ? <QuinceIcon iconSvg={CheckIcon} className={inStockIconCss} /> : null}
        <span>{t('in_stock_and_ready_to_ship')}</span>
      </div>
    </div>
  )
}

/**
 * DS twin of legacy `HolidayMessage`. Resolves holiday copy from `useStatsigDynamicHolidayConfig`
 * (reused verbatim) by matching `eddDetail.displayTags` against the enabled config tags. Renders one
 * row per matching tag, with the tag's icon (mobile only) and configured text color. Returns null
 * when no tag matches — identical logic/shape to legacy `HolidayMessage`.
 */
export const HolidayMessageDS = ({
  className,
  eddDetail,
}: {
  className?: string
  eddDetail?: { displayTags?: string[] }
}) => {
  const { locale } = useLocaleContext()
  const { deviceType } = useDeviceInfo()
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  const holidayMessages: { message: string; icon?: string; textColor?: string }[] =
    eddDetail?.displayTags &&
    Array.isArray(eddDetail.displayTags) &&
    eddDetail.displayTags.length > 0 &&
    holidayConfig?.tags
      ? eddDetail.displayTags.flatMap(displayTag => {
          const tagConfig = holidayConfig.tags.find(tag => tag.tag === displayTag && tag.enabled)
          if (tagConfig?.message) {
            const message = tagConfig.message[locale] ?? Object.values(tagConfig.message)[0] ?? null
            if (message) {
              return [{ message, icon: tagConfig.icon, textColor: tagConfig.textColor }]
            }
          }
          return []
        })
      : []

  if (holidayMessages.length === 0) {
    return null
  }

  return (
    <>
      {holidayMessages.map((holidayMsg, index) => (
        <div key={index} className={cx(inStockMessageCss, className)}>
          <div className={holidayMessageRowCss} style={{ color: holidayMsg.textColor }}>
            {isMobile && holidayMsg.icon ? (
              // eslint-disable-next-line @next/next/no-img-element -- parity with legacy HolidayMessage <img>
              <img src={transformImageSrc(holidayMsg.icon)} alt="" className={holidayIconCss} />
            ) : null}
            <span>{holidayMsg.message}</span>
          </div>
        </div>
      ))}
    </>
  )
}

/**
 * DS twin of legacy `NonPhysicalProductDeliveryText` (gift cards etc.).
 */
export const NonPhysicalProductDeliveryTextDS = ({ className }: { className?: string }) => {
  const { t } = useTranslation('common')
  return <div className={cx(deliveryTextCss, className)}>{t('gift_card_delivery_info')}</div>
}

/**
 * DS twin of legacy `ProductDisplayText` — "Out of stock" + optional "Back in stock after <date>".
 */
export const ProductDisplayTextDS = ({ date, className, showNewPdpDesignV2, isQuickATCForMobile }: DisplayTextType) => {
  const { t } = useTranslation('common')
  // Legacy ProductDisplayText renders the OOS icon only when showNewPdpDesignV2 is set; on mobile
  // it shows (hidden on desktop via css).
  return (
    <div className={cx(displayTextCss, isQuickATCForMobile ? displayTextQuickATCCss : undefined, className)}>
      {showNewPdpDesignV2 ? <QuinceIcon iconSvg={OutOfStockIcon} className={oosIconCss} /> : null}
      {t(ProductCTAConstants.OUT_OF_STOCK)}
      {date ? t('var1_date', { var1: t(ProductCTAConstants.BACK_IN_STOCK), date }) : ''}
    </div>
  )
}

export const PRODUCT_CTA_TEST_ID = TEST_LOCATORS.PRODUCT_CTA
